<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
// import { useRoute, useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { env_config } from "../../config";
import { enterFalseFullScreen, toggleFalseFullScreen, leavePlayersPage } from "../utils/players";
import { v4 as uuidv4 } from "uuid";
import { getUserInfo, getAuthToken } from "../utils/auth";
import Player from "../components/Player.vue";
import Cam from "../components/Cam.vue";
import OvenLiveKit from "ovenlivekit";
import { RepositoryFactory } from "../repositories/RepositoryFactory";
import SubtitleSocket from "../services/SubtitleSocket.js";

const store = useStore();
console.log(store.state.isConfirmed);

const route = useRoute();
const roomId = route.params.id;
const roomName = route.query.roomName;

const { user_mode } = getUserInfo();
const userMode = ref(user_mode);

const RecordingsRepository = RepositoryFactory.get("recordings");

const cams = ref(["player-id1", "player-id2", "player-id3"]);
let index = ref(0);

let isFalseFullScreen = ref();
let recordingsDialogVisible = ref(false);
let recordingsDialogHeader = ref("");
let recordingsDialogText = ref("");
let recordingsDialogErrorDetail = ref("");

// Access players' functions by referencing them
const p1 = ref(null);
const p2 = ref(null);
const p3 = ref(null);

const players = [p1, p2, p3];

const activeUsers = ref(0);

// --- SUBTÍTULOS EN TIEMPO REAL (réplica del player local del master) ---
const transcriptByLang = ref({}); // { idioma: texto acumulado }
const subLangs = ref([]); // idiomas disponibles
const sourceLang = ref(null); // idioma de origen (sourceLanguage)
const subLang = ref(localStorage.getItem("subLang") || null); // idioma elegido por el espectador
const subsEnabled = ref(localStorage.getItem("subtitlesEnabled") !== "false");
const subFontSize = ref(parseFloat(localStorage.getItem("subFontSize")) > 0 ? parseFloat(localStorage.getItem("subFontSize")) : 1.2);
const subNumLines = ref(parseInt(localStorage.getItem("subNumLines")) > -1 ? parseInt(localStorage.getItem("subNumLines")) : 2);
const subConfigOpen = ref(false);
const subBox = ref(null);
const subScroll = ref(null);
let subSocket = null;

// max-height = nº líneas * line-height (1.5) * tamaño de fuente → muestra exactamente
// N líneas completas (sin cortar la de arriba) con auto-scroll al final
const SUB_LINE_HEIGHT = 1.5;
const subMaxHeight = computed(() => subNumLines.value * SUB_LINE_HEIGHT * subFontSize.value + "rem");

// Texto del idioma elegido (fallback al de origen y a vacío)
const transcript = computed(() => transcriptByLang.value[subLang.value] || transcriptByLang.value[sourceLang.value] || "");
function setSubLang(l) {
    subLang.value = l;
    localStorage.setItem("subLang", l);
}

function persistSubs() {
    localStorage.setItem("subFontSize", subFontSize.value);
    localStorage.setItem("subNumLines", subNumLines.value);
}
function toggleSubs() {
    subsEnabled.value = !subsEnabled.value;
    localStorage.setItem("subtitlesEnabled", subsEnabled.value);
}
function toggleSubConfig() {
    subConfigOpen.value = !subConfigOpen.value;
}
function subFontUp() {
    subFontSize.value = Math.round((subFontSize.value + 0.1) * 10) / 10;
    persistSubs();
}
function subFontDown() {
    if (subFontSize.value > 0.3) {
        subFontSize.value = Math.round((subFontSize.value - 0.1) * 10) / 10;
        persistSubs();
    }
}
function subLinesUp() {
    subNumLines.value += 1;
    persistSubs();
}
function subLinesDown() {
    if (subNumLines.value > 0) {
        subNumLines.value -= 1;
        persistSubs();
    }
}

// Auto-scroll al final cuando llega texto (muestra siempre las últimas líneas)
watch(transcript, () => {
    nextTick(() => {
        if (subScroll.value) subScroll.value.scrollTop = subScroll.value.scrollHeight;
    });
});

// Arrastre de la caja de subtítulos (ratón + táctil), igual que el master
let _subDragMove = null;
let _subDragStop = null;
function setupSubtitleDrag() {
    const el = subBox.value;
    if (!el) return;
    let offsetX = 0;
    let offsetY = 0;
    let dragging = false;
    const start = (e) => {
        dragging = true;
        const p = e.touches ? e.touches[0] : e;
        const r = el.getBoundingClientRect();
        offsetX = p.clientX - r.left;
        offsetY = p.clientY - r.top;
        // Congela el ancho actual: al soltar el centrado, el ancho "auto" dependería
        // de `left` y la caja se ensancharía. Fijándolo en px se mantiene estable.
        el.style.width = r.width + "px";
        el.style.cursor = "grabbing";
    };
    _subDragMove = (e) => {
        if (!dragging) return;
        const p = e.touches ? e.touches[0] : e;
        el.style.transform = "none"; // suelta el centrado al arrastrar
        el.style.bottom = "auto";
        el.style.right = "auto";
        el.style.left = p.clientX - offsetX + "px";
        el.style.top = p.clientY - offsetY + "px";
    };
    _subDragStop = () => {
        dragging = false;
        el.style.cursor = "grab";
    };
    el.addEventListener("mousedown", start);
    el.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("mousemove", _subDragMove);
    document.addEventListener("mouseup", _subDragStop);
    document.addEventListener("touchmove", _subDragMove, { passive: true });
    document.addEventListener("touchend", _subDragStop);
}
function teardownSubtitleDrag() {
    if (_subDragMove) {
        document.removeEventListener("mousemove", _subDragMove);
        document.removeEventListener("touchmove", _subDragMove);
    }
    if (_subDragStop) {
        document.removeEventListener("mouseup", _subDragStop);
        document.removeEventListener("touchend", _subDragStop);
    }
}

// Display only one player at a time
function setPlayers() {
    for (let i = 0; i < 3; i++) {
        let cam = document.getElementById(cams.value[i]);
        if (i != index.value) {
            cam.parentNode.style.display = "none";
        } else {
            cam.parentNode.style.display = "block";
        }
    }
}

// Get the same button of the 3 players to iterate them
function getButtons() {
    const previousButtons = document.querySelectorAll("button.op-button.op-previous-cam-button");
    const nextButtons = document.querySelectorAll("button.op-button.op-next-cam-button");
    const soundButtons = document.querySelectorAll("button.op-button.op-volume-button");
    const chatButtons = document.querySelectorAll("button.op-button.op-chat-button");
    const fullScreenButtons = document.querySelectorAll("button.op-button.op-fullscreen-button");
    const volumeSlider = document.querySelectorAll("div.op-volume-slider-container .op-volume-silder");
    return { previousButtons, nextButtons, soundButtons, chatButtons, fullScreenButtons, volumeSlider };
}

// Set fullscreen with support on all the browsers
// (this may be deprecated in the future if fullscreen is removed)
function setFullScreen(index) {
    const element = document.getElementById(cams.value[index]);
    const requestFullScreen = element.requestFullscreen || element.webkitRequestFullScreen || element.mozRequestFullScreen || element.msRequestFullScreen;
    requestFullScreen.call(element);
}

// --- BIDIRECTIONALITY --- //

// Connect to Websocket Server
const wsId = ref(uuidv4());
let state = ref();
let streamerId = ref();
let op = ref();

watch(state, (currentValue) => {
    if (currentValue == "stopped") {
        if (op.value) {
            op.value.remove();
        }
    }
});

function changeStateToStopped() {
    state.value = "stopped";
}

function changeStateToStreaming() {
    state.value = "streaming";
}

function communicateState(emergency = "false") {
    connection.send(JSON.stringify({ state: state.value, emergency: emergency }));
}

console.log("Starting connection to WebSocket Server...");

// const connection = new WebSocket(`ws://localhost:8001/ws/webcam/${roomId}?ws-id=${wsId.value}`);
const { hostname } = new URL(env_config.API_BASE_DOMAIN);
const connection = new WebSocket(`wss://${hostname}/ws/webcam/${roomId}?ws-id=${wsId.value}`);

connection.onopen = function () {
    console.log("Successfully connected to the WebSocket Server");
};

connection.onmessage = (event) => {
    const data = JSON.parse(event.data);
    // console.log(data);
    state.value = data.state;
    streamerId.value = data.ws_id;
};

connection.onerror = function () {
    console.log("websocket error");
    if (connection) {
        changeStateToStopped();
        communicateState();
    }
};

// Remove OvenLiveKit streaming if connection with WebSocket closes
connection.onclose = () => {
    if (op.value) {
        op.value.remove();
        changeStateToStopped();
        communicateState();
    }
};

function startCam() {
    /* OvenLiveKit config */
    var config = {
        callbacks: {
            connected: () => {
                console.log("CALLBACK CONNECTED");
                changeStateToStreaming();
                communicateState();
            },
            connectionClosed: () => {
                console.log("CALLBACK CONNECTION CLOSED");
                connection.close();
                changeStateToStopped();
                communicateState();
            },
            error: (event) => {
                if (event == "Cannot create offer") {
                    console.log(event);
                }
            }
        }
    };

    /* OvenLiveKit initialization */
    let ovenLivekit = OvenLiveKit.create(config);
    op.value = ovenLivekit;
    ovenLivekit
        .getUserMedia({
            audio: true,
            video: true
        })
        .then(() => {
            if (connection.readyState !== WebSocket.CLOSED || connection.readyState !== WebSocket.CLOSING) {
                // ovenLivekit.startStreaming("wss://ome-test-2.bemyvega.dev/app/TZP2jwRJFmxjHDGgrGzb34-user?direction=send");
                ovenLivekit.startStreaming(`wss://${env_config.WEBRTC_ROOT}/app/${roomId}-user?direction=send`);
            } else {
                stopCam();
            }
        });
}
function stopCam() {
    changeStateToStopped();
    communicateState();
}

function emergencyStop() {
    connection.send(JSON.stringify({ state: "streaming", emergency: "true" }));
}

async function getStreamsStatistics() {
    try {
        const response = await RecordingsRepository.getStreamsStatistics(roomId);
        const api = response.data;
        activeUsers.value = api.totalConnections;

        return response.status;
    } catch (error) {
        const detail = error.response.data.detail;
        console.log(detail);
    }
}

const streamInterval = ref();
streamInterval.value = setInterval(() => {
    getStreamsStatistics();
}, 5000);

// Recordings
async function startRecording() {
    try {
        const response = await RecordingsRepository.startHLSDump(roomId);
        console.log(response.status);

        recordingsDialogVisible.value = true;

        recordingsDialogHeader.value = "Iniciando grabación";
        recordingsDialogText.value = `La grabación de la sesión "${roomName}" se ha iniciado correctamente.`;
        recordingsDialogErrorDetail.value = "";

        return response.status;
    } catch (error) {
        const detail = error.response.data.detail;
        console.log(error);

        recordingsDialogVisible.value = true;

        recordingsDialogHeader.value = `Error ${error.response.status}`;
        recordingsDialogText.value = `No se ha podido comenzar la grabación de la sesión "${roomName}".`;
        recordingsDialogErrorDetail.value = detail;
    }
}

async function stopRecording() {
    try {
        const response = await RecordingsRepository.stopHLSDump(roomId);
        console.log(response.status);

        recordingsDialogVisible.value = true;
        recordingsDialogHeader.value = "Terminando grabación";
        recordingsDialogText.value = `La grabación de la sesión "${roomName}" se ha completado correctamente.`;
        recordingsDialogErrorDetail.value = "";

        return response.status;
    } catch (error) {
        const detail = error.response.data.detail;
        console.log(error);

        recordingsDialogVisible.value = true;

        recordingsDialogHeader.value = `Error ${error.response.status}`;
        recordingsDialogText.value = `No se ha podido terminar la grabación de la sesión "${roomName}".`;
        recordingsDialogErrorDetail.value = detail;
    }
}

onMounted(() => {
    setPlayers();
    isFalseFullScreen.value = enterFalseFullScreen();

    // Subtítulos: solo espectadores autenticados (sin token, SubtitleSocket no conecta).
    subSocket = new SubtitleSocket({
        wsBase: env_config.SUBTITLES_WS_BASE,
        room: roomId,
        token: getAuthToken(),
        onUpdate: (state) => {
            transcriptByLang.value = state.byLang;
            subLangs.value = state.langs;
            if (!sourceLang.value) sourceLang.value = state.sourceLang;
            if (!subLang.value) subLang.value = state.sourceLang || state.langs[0] || null;
        },
    });
    subSocket.connect();
    setupSubtitleDrag();

    // Change cams
    document.body.addEventListener("click", function (event) {
        let { previousButtons, nextButtons } = getButtons();
        for (let i = 0; i < previousButtons.length; i++) {
            // Change to previous cam
            if (previousButtons[i].contains(event.target)) {
                index.value = index.value - 1;
                if (index.value < 0) {
                    index.value = 2;
                }
                setPlayers();
                if (window.innerHeight == screen.height) {
                    document.exitFullscreen();
                    setTimeout(() => setFullScreen(index.value), 500);
                }
                break;

                // Change to next cam
            } else if (nextButtons[i].contains(event.target)) {
                index.value = index.value + 1;
                if (index.value > 2) {
                    index.value = 0;
                }
                setPlayers();
                if (window.innerHeight == screen.height) {
                    document.exitFullscreen();
                    setTimeout(() => setFullScreen(index.value), 500);
                }
                break;
            }
        }
    });

    // Mute or unmute all players at once
    document.body.addEventListener("click", function (event) {
        let { soundButtons } = getButtons();
        for (let i = 0; i < soundButtons.length; i++) {
            if (soundButtons[i].contains(event.target)) {
                let isMute = players[i].value.getMute();
                for (let n = 0; n < 3; n++) {
                    players[n].value.setMute(isMute);
                }
                break;
            }
        }
    });

    // Control the volume of all players at once
    document.body.addEventListener("click", function (event) {
        let { volumeSlider } = getButtons();
        for (let i = 0; i < volumeSlider.length; i++) {
            if (volumeSlider[i].contains(event.target)) {
                let volume = players[i].value.getVolume();
                console.log("Volume is " + volume);
                for (let n = 0; n < 3; n++) {
                    players[n].value.setMute(false);
                    players[n].value.setVolume(volume);
                }
                break;
            }
        }
    });

    // Hide or display chat
    document.body.addEventListener("click", function (event) {
        let { chatButtons } = getButtons();
        for (let i = 0; i < chatButtons.length; i++) {
            if (chatButtons[i].contains(event.target)) {
                // let chat = document.getElementById("chat");
                let chat = document.querySelector("div.chat-column");
                if (chat.style.display != "none") {
                    chat.style.display = "none";
                } else {
                    chat.style.display = "block";
                }
                break;
            }
        }
    });

    // Overwrite fullscreen mode with false fullscreen
    document.body.addEventListener(
        "click",
        function (event) {
            let { fullScreenButtons } = getButtons();
            for (let i = 0; i < fullScreenButtons.length; i++) {
                if (fullScreenButtons[i].contains(event.target)) {
                    event.stopPropagation();
                    isFalseFullScreen.value = toggleFalseFullScreen(isFalseFullScreen.value);
                    if (window.innerHeight == screen.height) {
                        document.exitFullscreen();
                    }
                }
            }
        },
        true
    );
});

onUnmounted(() => {
    if (subSocket) subSocket.close();
    teardownSubtitleDrag();
    if (state.value == "streaming" && wsId.value == streamerId.value) {
        stopCam();
    }
    isFalseFullScreen.value = leavePlayersPage();
    if (streamInterval.value) {
        clearInterval(streamInterval.value);
    }
});
</script>

<template>
    <main>
        <Dialog v-model:visible="recordingsDialogVisible" :header="recordingsDialogHeader" style="width: 50vw" modal>
            <p class="m-0">{{ recordingsDialogText }}</p>
            <br />
            <p v-if="recordingsDialogErrorDetail != ''" class="m-0">Details: {{ recordingsDialogErrorDetail }}</p>
            <template #footer>
                <Button label="OK" icon="pi pi-check" autofocus @click="recordingsDialogVisible = false" />
            </template>
        </Dialog>
        <div>
            <div class="container sm:flex sm:flex-wrap md:grid md:grid-nogutter">
                <div class="player-column sm:col-12 md:col">
                    <!-- Players -->
                    <Player :id="cams[0]" ref="p1" :text="roomName + '<br><br>' + 'Profesor'" :sources="[{ type: 'webrtc', file: `wss://${env_config.WEBRTC_ROOT}/app/${roomId}-ptz` }]" />
                    <Player :id="cams[1]" ref="p2" :text="roomName + '<br><br>' + 'Pizarra'" :sources="[{ type: 'webrtc', file: `wss://${env_config.WEBRTC_ROOT}/app/${roomId}-fixed` }]" />
                    <Player :id="cams[2]" ref="p3" :text="roomName + '<br><br>' + 'Presentacion'" :sources="[{ type: 'webrtc', file: `wss://${env_config.WEBRTC_ROOT}/app/${roomId}-desktop` }]" />
                    <div id="floating-cam" class="floating-window sm:relative sm:col-12 sm:bottom-0 sm:left-0 sm:py-0 sm:w-20rem sm:h-12rem sm:m-auto sm:mb-0 sm:p-0 md:absolute md:bottom-0 md:right-0 md:left-0 md:w-20rem md:h-12rem md:mb-7 md:ml-2">
                        <Cam :id="`player-user-${roomId}`" :controls="false" :sources="[{ type: 'webrtc', file: `wss://${env_config.WEBRTC_ROOT}/app/${roomId}-user` }]" />
                    </div>

                    <!-- Overlay de subtítulos (réplica del player local del master): caja negra arrastrable, transcript acumulado con auto-scroll -->
                    <div v-show="subsEnabled && subNumLines > 0 && transcript" id="subtitle-overlay" ref="subBox" class="sub-float sub-style sub-draggable" aria-live="polite">
                        <div ref="subScroll" class="sub-scroll" :style="{ fontSize: subFontSize + 'rem', maxHeight: subMaxHeight }">
                            <span class="sub-twolines">{{ transcript }}</span>
                        </div>
                    </div>

                    <!-- Controles de subtítulos: flotantes fijos sobre el vídeo (no dependen del footer del player) -->
                    <div id="subtitle-controls">
                        <button type="button" class="sub-ctrl-btn" :aria-label="$t('subtitles.toggle')" @click="toggleSubs">
                            <i :class="subsEnabled ? 'pi pi-comment' : 'pi pi-comments'"></i>
                        </button>
                        <button type="button" class="sub-ctrl-btn" :aria-label="$t('subtitles.config')" @click="toggleSubConfig">
                            <i class="pi pi-cog"></i>
                        </button>
                    </div>

                    <!-- Panel de configuración de subtítulos (tamaño + nº de líneas + idioma, persistido) -->
                    <div v-show="subConfigOpen" id="subtitle-config" class="sub-config">
                        <h3>{{ $t("subtitles.config") }}</h3>
                        <div class="sub-preview sub-style"><span class="sub-twolines" :style="{ fontSize: subFontSize + 'rem' }">Aa</span></div>
                        <div class="sub-config-row">
                            <span>{{ $t("subtitles.size") }}</span>
                            <button type="button" @click="subFontDown">-</button>
                            <button type="button" @click="subFontUp">+</button>
                        </div>
                        <div class="sub-config-row">
                            <span>{{ $t("subtitles.lines") }}</span>
                            <button type="button" @click="subLinesDown">-</button>
                            <span class="sub-config-val">{{ subNumLines }}</span>
                            <button type="button" @click="subLinesUp">+</button>
                        </div>
                        <div v-if="subLangs.length > 1" class="sub-config-row">
                            <span>{{ $t("subtitles.language") }}</span>
                            <select class="sub-lang-select" :value="subLang" @change="setSubLang($event.target.value)">
                                <option v-for="l in subLangs" :key="l" :value="l">{{ l }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="player-footer mt-2 grid mr-0">
                        <p class="mx-auto col-12 text-center" style="color: white">
                            <i class="pi pi-eye mr-2"></i>
                            <b>{{ activeUsers }}</b> viewers
                        </p>
                        <div class="col-12 grid justify-content-center gap-2">
                            <Button v-if="userMode == 'teacher'" id="emergency-stop-button" class="p-button-danger mr-6" label="Force Stop" @click="emergencyStop"></Button>
                            <!-- <Button id="start-cam-button" label="Start Cam" :disabled="state == `streaming`" :class="state == `streaming` ? `inactive` : `active`" @click="startCam"></Button>
                            <Button id="stop-cam-button" label="Stop Cam" :disabled="state != `streaming` || wsId != streamerId" :class="state == `streaming` && wsId == streamerId ? `active` : `inactive`" @click="stopCam"></Button>
                            --><Button v-if="userMode == 'teacher'" id="start-recording" class="p-button-success ml-6" label="Start recording" @click="startRecording"></Button>
                            <Button v-if="userMode == 'teacher'" id="stop-recording" class="p-button-danger mr-6" label="Stop recording" @click="stopRecording"></Button>
                        </div>
                    </div>
                </div>

                <!-- Chat -->
                <div class="chat-column sm:col-12 sm:h-screen md:col-3">
                    <iframe id="chat" class="w-full sm:h-30rem sm:min-w-0 md:h-screen" src="https://socketio.bemyvega.com"> </iframe>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
main {
    background-color: black;
    height: 100vh;
}

.container {
    background-color: black;
}

.player-column {
    padding: 0;
    position: relative; /* ancla del overlay de subtítulos */
}

/* --- Subtítulos: réplica del player local del master (StreamPage.vue) --- */
.sub-float {
    position: fixed; /* anclado al viewport, por encima de cualquier capa del player */
    bottom: 5rem; /* por encima de la barra de controles del player */
    left: 50%;
    transform: translateX(-50%); /* centrado horizontal */
    max-width: 80vw;
    z-index: 2147483000;
    text-align: center;
}
/* Área de scroll interior: el padding queda en .sub-float (exterior), así el
   interlineado sobre la línea superior evita que se corten los glifos. */
.sub-scroll {
    overflow-y: auto;
    scrollbar-width: none; /* Firefox */
    line-height: 1.5; /* debe coincidir con SUB_LINE_HEIGHT en el script */
}
.sub-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
}
.sub-style {
    background-color: rgb(0, 0, 0);
    color: #fff;
    padding: 8px 14px;
    border-radius: 5px;
}
.sub-draggable {
    cursor: grab;
}
.sub-twolines {
    display: block;
    word-wrap: break-word;
}

/* Controles flotantes (toggle + engranaje), fijos sobre el vídeo */
#subtitle-controls {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 2147483000;
    display: flex;
    gap: 0.5rem;
}
.sub-ctrl-btn {
    width: 2.6rem;
    height: 2.6rem;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sub-ctrl-btn:hover {
    background: rgba(0, 0, 0, 0.8);
}

/* Panel de configuración de subtítulos */
#subtitle-config {
    position: fixed;
    top: 4rem;
    right: 1rem;
    z-index: 2147483000;
    background: rgba(0, 0, 0, 0.9);
    color: #fff;
    padding: 1rem 1.2rem;
    border-radius: 6px;
    min-width: 14rem;
}
#subtitle-config h3 {
    margin: 0 0 0.6rem;
    font-size: 1rem;
}
.sub-preview {
    display: inline-block;
    margin-bottom: 0.6rem;
}
.sub-config-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.4rem;
}
.sub-config-row span:first-child {
    flex: 1;
}
.sub-config-row button {
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 4px;
    background: #fff;
    color: #000;
    font-weight: 700;
    cursor: pointer;
}
.sub-config-val {
    min-width: 1.5rem;
    text-align: center;
}
.sub-lang-select {
    flex: 1;
    height: 2rem;
    border: none;
    border-radius: 4px;
    background: #fff;
    color: #000;
    padding: 0 0.4rem;
}
.chat-column {
    padding: 0;
    background-color: black;
}
#chat {
    width: 100%;
    min-height: 30rem;
}

#floating-cam {
    z-index: 10;
}

/* Bidirectionality */
.cam-buttons {
    display: flex;
    justify-content: center;
    background-color: black;
}

.active {
    background-color: green;
    color: white;
    cursor: pointer;
}

.inactive {
    background-color: lightgrey;
    color: darkgrey;
    cursor: none;
}
</style>
